/**
 * Post-signup confirmation copy (spec section 10, "Owner thank you page" and
 * "Provider thank you page").
 *
 * Both confirmation headlines are the approved copy and are reproduced exactly.
 *
 * Two things these pages must not do:
 *
 * - **Promise more than the terms.** The Founding Provider offer is the only
 *   novel legal exposure in the project, so the two-step explanation here says
 *   what section 09 says and links to the terms that govern it (CLAUDE.md hard
 *   rule 6).
 * - **Promise a message to someone who did not ask for one.** Nothing is ever
 *   sent to a person named in a recommendation (spec section 17). The referral
 *   block states that plainly, because it is the first question an owner asks.
 *
 * The referral ask and the share prompt that follow the confirmation are real
 * now. Their copy lives in `referral.ts`, next to the form that renders it.
 */

export interface ThankYouStep {
  label: string;
  title: string;
  body: string;
}

export const thankYouContent = {
  owner: {
    /** Where a successful owner signup lands. */
    path: '/thank-you/owner',
    /** Where this page sends someone who arrives without having signed up. */
    formPath: '/owners',
    eyebrow: 'Early access',
    /** Spec section 10. Approved copy. */
    headline: "You're in. Now help me find the good ones.",
    body: [
      "You're on the Maryland early access list. I'll be in touch as this reaches your part of the state.",
      'Nothing else is needed from you today.',
    ],
  },

  provider: {
    path: '/thank-you/provider',
    formPath: '/providers',
    eyebrow: 'Early access',
    /** Spec section 10. Approved copy. */
    headline: "You're a Founding Provider.",
    body: [
      "Your spot's reserved. Here's what that means, in two steps.",
      'Nothing else is needed from you today.',
    ],
    steps: [
      {
        label: 'Step one, done',
        title: "Your spot's reserved",
        body: "You told me who you are, what you do, and where you work. That reserves your Founding Provider status. There's nothing to pay and nothing to set up.",
      },
      {
        label: 'Step two, at launch',
        title: 'You claim and complete your profile',
        body: "When the Maryland network is ready for horse owners, I come back to you with an invitation to claim your profile and finish it. Your services, your service area, and how you want to be reached. That's when your Founding Provider status and upgraded profile go live.",
      },
    ] satisfies readonly ThankYouStep[],
    termsNote: {
      prefix: 'The program is described in full in the',
      linkLabel: 'site terms',
      linkTo: '/terms',
      suffix: ', which govern it.',
    },
  },

  backLinkLabel: 'Back to the homepage',
} as const;
